package org.gymly.ui.admin

import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Switch
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.delay
import org.gymly.data.AdminRepo
import org.gymly.model.AsistenteCatalogo
import org.gymly.model.AsistenteDatosProveedor
import org.gymly.model.AsistenteProveedor
import org.gymly.theme.AdminColors
import org.gymly.util.userMessage

private const val TIPO_GEMINI = "GEMINI"
private const val TIPO_OPENAI = "OPENAI"

/** Pausa tras escribir la clave antes de pedir los modelos: evita una consulta por cada tecla. */
private const val PAUSA_MODELOS_MS = 600L
private const val LARGO_MINIMO_CLAVE = 8

/**
 * Crear o editar una cuenta de IA. El proveedor se elige del catálogo, y los modelos disponibles
 * se cargan solos con la clave: no hay que escribir el nombre del modelo a mano.
 */
@OptIn(ExperimentalLayoutApi::class)
@Composable
internal fun ProveedorDialog(
    inicial: AsistenteProveedor?,
    catalogo: List<AsistenteCatalogo>,
    ocupado: Boolean,
    error: String?,
    onGuardar: (AsistenteDatosProveedor) -> Unit,
    onCerrar: () -> Unit,
) {
    var nombre by remember { mutableStateOf(inicial?.nombre ?: "") }
    var tipo by remember { mutableStateOf(inicial?.tipo ?: TIPO_GEMINI) }
    var baseUrl by remember { mutableStateOf(inicial?.baseUrl.orEmpty()) }
    var modelo by remember { mutableStateOf(inicial?.modelo ?: "") }
    var clave by remember { mutableStateOf("") }
    var precioEntrada by remember { mutableStateOf(centavosATexto(inicial?.precioEntradaPorMillon ?: 0L)) }
    var precioSalida by remember { mutableStateOf(centavosATexto(inicial?.precioSalidaPorMillon ?: 0L)) }
    var maxTokens by remember { mutableStateOf((inicial?.maxTokensSalida ?: 1024).toString()) }
    var tokensDiarios by remember { mutableStateOf((inicial?.limiteDiarioTokens ?: 0L).toString()) }
    var limite by remember { mutableStateOf(centavosATexto(inicial?.limiteMensualCents ?: 0L)) }
    var activo by remember { mutableStateOf(inicial?.activo ?: false) }
    var textoInvalido by remember { mutableStateOf<String?>(null) }
    var modelos by remember { mutableStateOf<List<String>>(emptyList()) }
    var cargandoModelos by remember { mutableStateOf(false) }
    var errorModelos by remember { mutableStateOf<String?>(null) }
    var menuModelos by remember { mutableStateOf(false) }

    // Clave guardada y mismo proveedor: los modelos se cargan sin que escriba nada.
    val usaClaveGuardada = inicial?.tieneClave == true && inicial.tipo == tipo && clave.isBlank()

    LaunchedEffect(tipo, baseUrl, clave) {
        if (!usaClaveGuardada && clave.trim().length < LARGO_MINIMO_CLAVE) {
            modelos = emptyList()
            errorModelos = null
            return@LaunchedEffect
        }
        delay(PAUSA_MODELOS_MS)
        cargandoModelos = true
        errorModelos = null
        try {
            modelos = AdminRepo.api.modelosDisponiblesAsistente(
                tipo = tipo,
                baseUrl = baseUrl.trim().ifBlank { null },
                clave = clave.trim().ifBlank { null },
                proveedorId = inicial?.id,
            )
            if (modelo.isBlank()) modelo = modelos.firstOrNull().orEmpty()
        } catch (e: Throwable) {
            modelos = emptyList()
            errorModelos = e.userMessage("No se pudieron cargar los modelos. Revisa la clave y la dirección.")
        } finally {
            cargandoModelos = false
        }
    }

    fun elegirProveedor(item: AsistenteCatalogo) {
        tipo = item.tipo
        baseUrl = item.baseUrl.orEmpty()
        if (nombre.isBlank()) nombre = item.nombre
        // Otro proveedor: el modelo anterior ya no aplica. Se elige de la nueva lista.
        modelo = ""
        modelos = emptyList()
    }

    fun guardar() {
        val entrada = importeACentavos(precioEntrada)
        val salida = importeACentavos(precioSalida)
        val tope = importeACentavos(limite)
        val maximoRespuesta = maxTokens.trim().toIntOrNull()
        val diario = tokensDiarios.trim().toLongOrNull()
        val direccion = baseUrl.trim()
        when {
            nombre.isBlank() -> textoInvalido = "Escribe un nombre para la cuenta"
            modelo.isBlank() -> textoInvalido = "Elige un modelo de la lista o escríbelo"
            tipo == TIPO_OPENAI && !direccion.startsWith("https://") ->
                textoInvalido = "La dirección del proveedor debe empezar con https://"
            entrada == null || salida == null || tope == null ->
                textoInvalido = "Revisa los importes: un número con hasta dos decimales"
            maximoRespuesta == null || maximoRespuesta !in 64..8192 ->
                textoInvalido = "El máximo por respuesta debe estar entre 64 y 8192"
            diario == null || diario < 0 -> textoInvalido = "El tope diario debe ser 0 o más"
            else -> {
                textoInvalido = null
                onGuardar(AsistenteDatosProveedor(
                    nombre = nombre.trim(),
                    tipo = tipo,
                    baseUrl = if (tipo == TIPO_OPENAI) direccion else null,
                    modelo = modelo.trim(),
                    activo = activo,
                    limiteMensualCents = tope,
                    limiteDiarioTokens = diario,
                    maxTokensSalida = maximoRespuesta,
                    precioEntradaPorMillon = entrada,
                    precioSalidaPorMillon = salida,
                    clave = clave,
                ))
            }
        }
    }

    DialogoFormulario(
        titulo = if (inicial == null) "Nueva cuenta de IA" else "Editar cuenta de IA",
        onCerrar = onCerrar,
        cuerpo = {
            Text("Proveedor", color = AdminColors.TextMuted, fontSize = 12.sp)
            FlowRow(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                verticalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                catalogo.forEach { item ->
                    val seleccionado = item.tipo == tipo && item.baseUrl.orEmpty() == baseUrl.trim()
                    OutlinedButton(onClick = { elegirProveedor(item) }) {
                        Text(item.nombre, fontWeight = if (seleccionado) FontWeight.Bold else null)
                    }
                }
            }

            OutlinedTextField(
                nombre, { nombre = it }, label = { Text("Nombre de la cuenta") },
                singleLine = true, modifier = Modifier.fillMaxWidth(),
            )
            AyudaCampo("Cómo la reconocerás en la lista. Por ejemplo: Gemini de pruebas.")

            if (tipo == TIPO_OPENAI) {
                OutlinedTextField(
                    baseUrl, { baseUrl = it; modelo = "" }, label = { Text("Dirección del proveedor (https)") },
                    singleLine = true, modifier = Modifier.fillMaxWidth(),
                )
            }

            val claveGuardada = inicial?.tieneClave == true
            OutlinedTextField(
                clave, { clave = it },
                label = { Text("Clave de API") },
                placeholder = { if (claveGuardada) Text("•••••••• guardada") },
                singleLine = true, modifier = Modifier.fillMaxWidth(),
            )
            AyudaCampo(
                if (claveGuardada) {
                    "Ya hay una clave guardada. Déjala vacía para conservarla, o escribe una nueva para cambiarla. Por seguridad no se vuelve a mostrar."
                } else {
                    "Se guarda cifrada. Al escribirla se cargan los modelos que tiene disponibles."
                },
            )

            Text("Modelo", color = AdminColors.TextMuted, fontSize = 12.sp)
            Box(Modifier.fillMaxWidth()) {
                OutlinedButton(
                    onClick = { menuModelos = true },
                    enabled = modelos.isNotEmpty(),
                    modifier = Modifier.fillMaxWidth(),
                ) {
                    Text(if (modelo.isBlank()) "Elegir modelo" else modelo)
                }
                DropdownMenu(expanded = menuModelos, onDismissRequest = { menuModelos = false }) {
                    Column(Modifier.heightIn(max = 360.dp).verticalScroll(rememberScrollState())) {
                        modelos.forEach { disponible ->
                            DropdownMenuItem(
                                text = { Text(disponible) },
                                onClick = {
                                    modelo = disponible
                                    menuModelos = false
                                },
                            )
                        }
                    }
                }
            }
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                if (cargandoModelos) {
                    CircularProgressIndicator(Modifier.size(14.dp), strokeWidth = 2.dp)
                    AyudaCampo("Cargando los modelos disponibles…")
                } else if (modelos.isNotEmpty()) {
                    AyudaCampo("${modelos.size} modelos disponibles. Elige uno de la lista.")
                } else if (errorModelos != null) {
                    Text(errorModelos.orEmpty(), color = AdminColors.Error, fontSize = 11.sp)
                } else {
                    AyudaCampo("Escribe la clave y aquí aparecerán los modelos del proveedor.")
                }
            }

            OutlinedTextField(
                modelo, { modelo = it }, label = { Text("…o escribe el nombre exacto del modelo") },
                singleLine = true, modifier = Modifier.fillMaxWidth(),
            )

            Text("Costo (USD por millón de tokens)", color = AdminColors.TextMuted, fontSize = 12.sp)
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                OutlinedTextField(precioEntrada, { precioEntrada = it }, label = { Text("Entrada") }, singleLine = true, modifier = Modifier.weight(1f))
                OutlinedTextField(precioSalida, { precioSalida = it }, label = { Text("Salida") }, singleLine = true, modifier = Modifier.weight(1f))
            }
            AyudaCampo("Lo que cobra el proveedor por cada millón de tokens que entran (preguntas) y salen (respuestas). Los precios no se consultan solos: cópialos de su lista oficial.")

            Text("Topes de uso", color = AdminColors.TextMuted, fontSize = 12.sp)
            OutlinedTextField(
                maxTokens, { maxTokens = it },
                label = { Text("Máximo de tokens por respuesta") },
                singleLine = true, modifier = Modifier.fillMaxWidth(),
            )
            AyudaCampo("Cuánto puede escribir una sola respuesta, de 64 a 8192 tokens. Un token es aprox. tres cuartas partes de una palabra. 1024 es razonable.")

            OutlinedTextField(
                tokensDiarios, { tokensDiarios = it },
                label = { Text("Tokens por día (0 = sin tope)") },
                singleLine = true, modifier = Modifier.fillMaxWidth(),
            )
            AyudaCampo("Cuántos tokens puede gastar esta cuenta en un día. Al llegar, deja de responder hasta el día siguiente.")

            OutlinedTextField(
                limite, { limite = it },
                label = { Text("Gasto mensual máximo en USD (0 = sin límite)") },
                singleLine = true, modifier = Modifier.fillMaxWidth(),
            )
            AyudaCampo("Tope de dinero del mes. Con 0 no hay límite. Con precios en 0 el gasto aparece como 0 y el límite no protege.")

            Row(verticalAlignment = Alignment.CenterVertically) {
                Text("Cuenta activa", color = AdminColors.Text, fontWeight = FontWeight.SemiBold, modifier = Modifier.weight(1f))
                Switch(checked = activo, onCheckedChange = { activo = it })
            }
            AyudaCampo("Solo una cuenta activa con clave puede responder. Actívala cuando termines de configurarla.")

            (textoInvalido ?: error)?.let { Text(it, color = AdminColors.Error, fontSize = 12.sp) }
        },
        pie = {
            TextButton(onClick = onCerrar) { Text("Cancelar") }
            Spacer(Modifier.size(8.dp))
            Button(onClick = { guardar() }, enabled = !ocupado) { Text("Guardar") }
        },
    )
}
